// =======================
// HIMS API CLIENT
// =======================

const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'hims_token';
const USER_KEY = 'hims_user';

// -----------------------
// TOKEN STORAGE
// -----------------------
export const tokenStorage = {
  get: () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(TOKEN_KEY);
    }
    return null;
  },

  set: (token) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(TOKEN_KEY, token);
    }
  },

  clear: () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }
  },

  getUser: () => {
    if (typeof window !== 'undefined') {
      const user = localStorage.getItem(USER_KEY);
      return user ? JSON.parse(user) : null;
    }
    return null;
  },

  setUser: (user) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }
  },
};

// -----------------------
// ERROR HANDLING
// -----------------------
export class ApiError extends Error {
  constructor(message, status, details) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

export function parseApiError(data) {
  if (!data) return 'Bir sorun oluştu';

  if (typeof data.detail === 'string') {
    return data.detail;
  }

  if (Array.isArray(data.detail)) {
    return data.detail
      .map((e) => {
        const field = e.loc?.slice(1).join('.') || 'alan';
        return `${field}: ${e.msg}`;
      })
      .join(' | ');
  }

  return 'Bir sorun oluştu';
}

// Türkçe hata mesajları sözlüğü
const ERROR_MESSAGES_TR = {
  // Genel hatalar
  'Network Error': 'İnternet bağlantınızı kontrol edin',
  'Failed to fetch': 'Sunucuya bağlanılamadı. Lütfen internet bağlantınızı kontrol edin',
  'Unexpected error': 'Bir sorun oluştu. Lütfen tekrar deneyin',
  'Internal Server Error': 'Sunucu hatası. Lütfen daha sonra tekrar deneyin',
  'Service Unavailable': 'Sistem şu an kullanılamıyor. Lütfen birkaç dakika sonra tekrar deneyin',
  'Unknown error': 'Bir sorun oluştu. Lütfen tekrar deneyin',
  'Bilinmeyen hata': 'Bir sorun oluştu. Lütfen tekrar deneyin',
  
  // Kimlik doğrulama - LOGIN
  'Invalid credentials': 'E-posta veya şifre hatalı',
  'Invalid email or password': 'E-posta veya şifre hatalı',
  'Incorrect email or password': 'E-posta veya şifre hatalı',
  'Wrong password': 'Şifre hatalı',
  'Token expired': 'Oturumunuz sona erdi. Lütfen tekrar giriş yapın',
  'Not authenticated': 'Oturumunuz sona erdi. Lütfen tekrar giriş yapın',
  'Unauthorized': 'Bu işlem için yetkiniz yok',
  'Forbidden': 'Bu işlemi yapmaya yetkiniz yok',
  'Access denied': 'Erişim reddedildi',
  'Account disabled': 'Hesabınız devre dışı. Yöneticinizle iletişime geçin',
  'Account locked': 'Hesabınız kilitlendi. Yöneticinizle iletişime geçin',
  
  // Kullanıcı işlemleri
  'Email already registered': 'Bu email adresi zaten kayıtlı',
  'Bu email adresi zaten kayıtlı': 'Bu email adresi zaten kullanılıyor',
  'User not found': 'Bu email ile kayıtlı kullanıcı bulunamadı',
  'Invalid password': 'Şifre hatalı',
  'Password too short': 'Şifre en az 6 karakter olmalıdır',
  'Invalid email format': 'Geçerli bir email adresi girin',
  'Phone number already exists': 'Bu telefon numarası zaten kayıtlı',
  
  // Kayıt işlemleri
  'Record not found': 'Kayıt bulunamadı',
  'Already exists': 'Bu kayıt zaten mevcut',
  'Cannot delete': 'Bu kayıt silinemez',
  'Validation error': 'Lütfen tüm zorunlu alanları doldurun',
  
  // Dosya işlemleri
  'File too large': 'Dosya boyutu çok büyük (maksimum 5MB)',
  'Invalid file type': 'Desteklenmeyen dosya formatı',
  'Upload failed': 'Dosya yüklenemedi. Lütfen tekrar deneyin',
  
  // OCPP / Şarj işlemleri
  'Station offline': 'İstasyon çevrimdışı',
  'Charger not available': 'Şarj cihazı şu an kullanılamıyor',
  'Insufficient balance': 'Yetersiz bakiye',
  'Card not found': 'Kart bulunamadı',
  'Transaction failed': 'İşlem başarısız oldu',
  
  // Teknisyen / İş emri
  'Technician not found': 'Teknisyen bulunamadı',
  'Work order not found': 'İş emri bulunamadı',
  'Cannot assign': 'Atama yapılamadı',
};

// Hata mesajını Türkçeye çevir
function translateError(message) {
  if (!message) return 'Bir hata oluştu';
  
  // Direkt eşleşme
  if (ERROR_MESSAGES_TR[message]) {
    return ERROR_MESSAGES_TR[message];
  }
  
  // Kısmi eşleşme
  const lowerMsg = message.toLowerCase();
  for (const [key, value] of Object.entries(ERROR_MESSAGES_TR)) {
    if (lowerMsg.includes(key.toLowerCase())) {
      return value;
    }
  }
  
  // Login hatası kontrolü
  if (lowerMsg.includes('401') || lowerMsg.includes('unauthorized') || lowerMsg.includes('invalid')) {
    return 'E-posta veya şifre hatalı';
  }
  
  // Email hatası kontrolü
  if (lowerMsg.includes('email') && (lowerMsg.includes('exist') || lowerMsg.includes('kayıtlı') || lowerMsg.includes('registered'))) {
    return 'Bu email adresi zaten kullanılıyor';
  }
  
  // Bağlantı hatası kontrolü
  if (lowerMsg.includes('network') || lowerMsg.includes('fetch') || lowerMsg.includes('connection') || lowerMsg.includes('failed')) {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin';
  }
  
  // Yetki hatası kontrolü
  if (lowerMsg.includes('permission') || lowerMsg.includes('yetki') || lowerMsg.includes('izin') || lowerMsg.includes('403')) {
    return 'Bu işlem için yetkiniz yok';
  }
  
  // Zaten var kontrolü
  if (lowerMsg.includes('already') || lowerMsg.includes('zaten') || lowerMsg.includes('mevcut')) {
    return 'Bu kayıt zaten mevcut';
  }
  
  // Bulunamadı kontrolü
  if (lowerMsg.includes('not found') || lowerMsg.includes('bulunamadı') || lowerMsg.includes('404')) {
    return 'Kayıt bulunamadı';
  }
  
  // Sunucu hatası kontrolü
  if (lowerMsg.includes('500') || lowerMsg.includes('server error') || lowerMsg.includes('internal')) {
    return 'Sunucu hatası. Lütfen daha sonra tekrar deneyin';
  }
  
  // Türkçe mesajları olduğu gibi döndür (Bilinmeyen hata hariç)
  if (/[ğüşıöçĞÜŞİÖÇ]/.test(message) && !lowerMsg.includes('bilinmeyen')) {
    return message;
  }
  
  // Son çare: genel mesaj
  return 'Bir sorun oluştu. Lütfen tekrar deneyin';
}

export function getErrorMessage(error) {
  if (error instanceof ApiError) {
    return translateError(error.message);
  }
  if (error?.response?.data?.detail) {
    const detail = error.response.data.detail;
    if (typeof detail === 'string') {
      return translateError(detail);
    }
    if (Array.isArray(detail) && detail[0]?.msg) {
      return translateError(detail[0].msg);
    }
    return translateError(parseApiError(error.response.data));
  }
  if (error instanceof Error) {
    return translateError(error.message);
  }
  if (typeof error === 'string') {
    return translateError(error);
  }
  return 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin';
}

// -----------------------
// HTTP CLIENT
// -----------------------
async function request(path, options = {}, auth = true) {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (auth) {
    const token = tokenStorage.get();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const url = `${BASE_URL}${path}`;

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    let data = null;
    const contentType = response.headers.get('content-type');
    
    // Try to read and parse response body
    try {
      const text = await response.text();
      if (text) {
        try {
          data = JSON.parse(text);
        } catch (e) {
          // Response is not JSON
          console.warn('Response is not JSON:', text.substring(0, 100));
        }
      }
    } catch (e) {
      // Body might already be read by platform interceptors (Emergent)
      console.warn('Could not read response body:', e.message);
    }

    if (!response.ok) {
      // Handle 401 Unauthorized - login errors
      if (response.status === 401) {
        // Don't clear token on login page - user might be trying to login
        if (!path.includes('/auth/login')) {
          tokenStorage.clear();
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
        }
        // Return the actual error message from backend
        const errorMsg = data?.detail || 'E-posta veya şifre hatalı';
        throw new ApiError(errorMsg, 401);
      }
      
      // Handle rate limiting (429) specially
      if (response.status === 429) {
        // Try to extract minutes from response if available
        let minutes = 15; // Default
        if (data && data.detail) {
          const match = data.detail.match(/(\d+)\s*dakika/i);
          if (match) {
            minutes = parseInt(match[1], 10);
          }
        }
        throw new ApiError(`Çok fazla deneme. ${minutes} dakika sonra tekrar deneyin.`, 429);
      }
      
      // Handle 400 (Bad Request) with detail message - OCPP rejection etc.
      if (response.status === 400 && data?.detail) {
        throw new ApiError(data.detail, 400);
      }
      
      // Handle 404 with specific message for OCPP operations
      if (response.status === 404 && path.includes('/ocpp/')) {
        const msg = data?.detail || 'Şarj cihazı bağlı değil veya bulunamadı';
        throw new ApiError(msg, 404);
      }
      
      // Handle 404 with detail message
      if (response.status === 404 && data?.detail) {
        throw new ApiError(data.detail, 404);
      }

      const errorMessage = translateError(parseApiError(data));
      const details = Array.isArray(data?.detail) ? data.detail : undefined;
      throw new ApiError(errorMessage, response.status, details);
    }

    return data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError('Bağlantı hatası oluştu', 0);
  }
}

// -----------------------
// AUTH SERVICE
// -----------------------
export const authService = {
  login: async (data) => {
    const response = await request(
      '/api/auth/login',
      { method: 'POST', body: JSON.stringify(data) },
      false
    );
    tokenStorage.set(response.access_token);
    tokenStorage.setUser(response.user);
    return response;
  },

  register: async (data) => {
    const response = await request(
      '/api/auth/register',
      { method: 'POST', body: JSON.stringify(data) },
      false
    );
    if (response.access_token) {
      tokenStorage.set(response.access_token);
      tokenStorage.setUser(response.user);
    }
    return response;
  },

  me: async () => {
    return request('/api/auth/me');
  },

  logout: async () => {
    // Try to invalidate token on backend
    try {
      await request('/api/auth/logout', { method: 'POST' });
    } catch (error) {
      // Ignore errors - we'll clear local storage anyway
      // console.log('Logout request failed, clearing local storage');
    }
    tokenStorage.clear();
  },

  changePassword: async (oldPassword, newPassword) => {
    const response = await request('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ old_password: oldPassword, new_password: newPassword })
    });
    // Update token if new one provided
    if (response.access_token) {
      tokenStorage.set(response.access_token);
    }
    return response;
  },

  isLoggedIn: () => {
    return !!tokenStorage.get();
  },

  getStoredUser: () => {
    return tokenStorage.getUser();
  },

  validateSession: async () => {
    if (!tokenStorage.get()) return null;
    try {
      const user = await authService.me();
      tokenStorage.setUser(user);
      return user;
    } catch {
      tokenStorage.clear();
      return null;
    }
  },
};

// -----------------------
// USERS SERVICE
// -----------------------
export const usersService = {
  list: () => request('/api/users'),
  get: (id) => request(`/api/users/${id}`),
  create: (data) => request('/api/users', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/users/${id}`, { method: 'DELETE' }),
};

// -----------------------
// SITES SERVICE
// -----------------------
export const sitesService = {
  list: () => request('/api/sites'),
  get: (id) => request(`/api/sites/${id}`),
  create: (data) => request('/api/sites', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/sites/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/sites/${id}`, { method: 'DELETE' }),
};

// -----------------------
// LOCATIONS SERVICE
// -----------------------
export const locationsService = {
  list: (accountId) => request(accountId ? `/api/locations?account_id=${accountId}` : '/api/locations'),
  get: (id) => request(`/api/locations/${id}`),
  create: (data) => request('/api/locations', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/locations/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/locations/${id}`, { method: 'DELETE' }),
};

// -----------------------
// STATIONS SERVICE
// -----------------------
export const stationsService = {
  list: () => request('/api/stations'),
  get: (id) => request(`/api/stations/${id}`),
  create: (data) => request('/api/stations', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/stations/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/stations/${id}`, { method: 'DELETE' }),
  control: (id, data) => request(`/api/stations/${id}/control`, { method: 'POST', body: JSON.stringify(data) }),
};

// -----------------------
// SESSIONS SERVICE
// -----------------------
export const sessionsService = {
  list: (isActive) => {
    let url = '/api/sessions';
    if (isActive !== undefined) {
      url += `?is_active=${isActive}`;
    }
    return request(url);
  },
  get: (id) => request(`/api/sessions/${id}`),
  create: (data) => request('/api/sessions', { method: 'POST', body: JSON.stringify(data) }),
  end: (id, data) => request(`/api/sessions/${id}/end`, { method: 'POST', body: JSON.stringify(data) }),
};

// -----------------------
// RFID SERVICE
// -----------------------
export const rfidService = {
  list: () => request('/api/rfid'),
  get: (cardId) => request(`/api/rfid/${cardId}`),
  create: (data) => request('/api/rfid', { method: 'POST', body: JSON.stringify(data) }),
  update: (cardId, data) => request(`/api/rfid/${cardId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (cardId) => request(`/api/rfid/${cardId}`, { method: 'DELETE' }),
  credit: (cardId, data) => request(`/api/rfid/${cardId}/load-credit`, { method: 'POST', body: JSON.stringify(data) }),
  recentScans: (limit = 20) => request(`/api/rfid/recent-scans?limit=${limit}`),
  unregisteredScans: (limit = 10) => request(`/api/rfid/unregistered-scans?limit=${limit}`),
  // Yeni eklenen endpointler
  getStatement: (cardId) => request(`/api/rfid/${cardId}/statement`),
  getOwnerInfo: (cardId) => request(`/api/rfid/${cardId}/owner-info`),
  getUserRfidSummary: (userId) => request(`/api/users/${userId}/rfid-summary`),
};

// -----------------------
// PRICING SERVICE
// -----------------------
export const pricingService = {
  get: () => request('/api/pricing'),
  update: (pricePerKwh, siteId = null) => {
    let url = `/api/pricing?price_per_kwh=${pricePerKwh}`;
    if (siteId) url += `&site_id=${siteId}`;
    return request(url, { method: 'POST' });
  },
};

// -----------------------
// DASHBOARD SERVICE
// -----------------------
export const dashboardService = {
  stats: (period = 'today', siteId = null) => {
    let url = `/api/dashboard/stats?period=${period}`;
    if (siteId) url += `&site_id=${siteId}`;
    return request(url);
  },
  live: (siteId = null) => {
    let url = '/api/dashboard/live';
    if (siteId) url += `?site_id=${siteId}`;
    return request(url);
  },
  revenueChart: (period = 'week') => request(`/api/dashboard/charts/revenue?period=${period}`),
  energyByStationChart: (period = 'week') => request(`/api/dashboard/charts/energy-by-station?period=${period}`),
  sessionsBySiteChart: (period = 'week') => request(`/api/dashboard/charts/sessions-by-site?period=${period}`),
};

// -----------------------
// SEARCH SERVICE
// -----------------------
export const searchService = {
  search: (query) => request(`/api/search?q=${encodeURIComponent(query)}`),
};

// -----------------------
// REPORTS SERVICE
// -----------------------
export const reportsService = {
  all: () => request('/api/reports/all'),
  user: (userId, startDate = null, endDate = null) => {
    let url = `/api/reports/user/${userId}`;
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;
    return request(url);
  },
  myStatement: (startDate = null, endDate = null) => {
    let url = '/api/reports/my-statement';
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;
    return request(url);
  },
  downloadPdf: async (userId, startDate = null, endDate = null) => {
    let url = `/api/reports/user/${userId}/pdf`;
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;
    
    const token = tokenStorage.get();
    const response = await fetch(`${BASE_URL}${url}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new ApiError('PDF indirilemedi', response.status);
    }
    
    return response.blob();
  },
  downloadMyPdf: async (startDate = null, endDate = null) => {
    let url = '/api/reports/my-statement/pdf';
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;
    
    const token = tokenStorage.get();
    const response = await fetch(`${BASE_URL}${url}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new ApiError('PDF indirilemedi', response.status);
    }
    
    return response.blob();
  },
  sendEmail: (data) => request('/api/reports/send-email', { 
    method: 'POST', 
    body: JSON.stringify(data) 
  }),
};

// -----------------------
// EMAIL SETTINGS SERVICE
// -----------------------
export const emailSettingsService = {
  get: () => request('/api/settings/email'),
  update: (data) => request('/api/settings/email', { 
    method: 'POST', 
    body: JSON.stringify(data) 
  }),
  test: (toEmail) => request(`/api/settings/email/test?to_email=${encodeURIComponent(toEmail)}`, { 
    method: 'POST' 
  }),
};

// -----------------------
// OCPP SERVICE
// -----------------------
export const ocppService = {
  status: () => request('/api/ocpp/status'),
  chargers: () => request('/api/ocpp/chargers'),
  remoteStart: (data) => request('/api/ocpp/remote-start', { method: 'POST', body: JSON.stringify(data) }),
  remoteStop: (data) => request('/api/ocpp/remote-stop', { method: 'POST', body: JSON.stringify(data) }),
  reset: async (data) => {
    try {
      return await request('/api/ocpp/reset', { method: 'POST', body: JSON.stringify(data) });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  charger: async (id) => {
    try {
      return await request(`/api/ocpp/chargers/${id}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  configuration: async (chargerId) => {
    try {
      return await request(`/api/ocpp/configuration/${chargerId}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
};

// -----------------------
// OCPP LOGS SERVICE
// -----------------------
export const ocppLogsService = {
  // Message logs
  getLogs: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.charge_point_id) queryParams.append('charge_point_id', params.charge_point_id);
    if (params.action) queryParams.append('action', params.action);
    if (params.direction) queryParams.append('direction', params.direction);
    if (params.status) queryParams.append('status', params.status);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    return request(`/api/ocpp-logs?${queryParams.toString()}`);
  },
  
  // Error logs
  getErrors: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.charge_point_id) queryParams.append('charge_point_id', params.charge_point_id);
    if (params.error_code) queryParams.append('error_code', params.error_code);
    if (params.severity) queryParams.append('severity', params.severity);
    if (params.acknowledged !== undefined) queryParams.append('acknowledged', params.acknowledged);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    return request(`/api/ocpp-logs/errors?${queryParams.toString()}`);
  },
  
  // Critical errors for banner
  getCriticalErrors: () => request('/api/ocpp-logs/errors/critical'),
  
  // Acknowledge error
  acknowledgeError: (errorId) => request(`/api/ocpp-logs/errors/${errorId}/acknowledge`, { method: 'POST' }),
  
  // RFID logs
  getRfidLogs: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.charge_point_id) queryParams.append('charge_point_id', params.charge_point_id);
    if (params.id_tag) queryParams.append('id_tag', params.id_tag);
    if (params.status) queryParams.append('status', params.status);
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    return request(`/api/ocpp-logs/rfid?${queryParams.toString()}`);
  },
  
  // Live charger status
  getLiveStatus: () => request('/api/ocpp-logs/live-status'),
  
  // Statistics
  getStats: (period = '24h') => request(`/api/ocpp-logs/stats?period=${period}`),
  
  // Delete old logs
  deleteOldLogs: (days = 30) => request(`/api/ocpp-logs/old?days=${days}`, { method: 'DELETE' }),
  
  // Seed mock data
  seedData: () => request('/api/ocpp-logs/seed', { method: 'POST' }),
  
  // Remote Control Operations
  remoteStart: (chargerId, idTag = 'DEFAULT', connectorId = 1) => 
    request('/api/ocpp/remote-start', { 
      method: 'POST', 
      body: JSON.stringify({ charge_point_id: chargerId, id_tag: idTag, connector_id: connectorId }) 
    }),
  
  remoteStop: (chargerId, transactionId) => 
    request('/api/ocpp/remote-stop', { 
      method: 'POST', 
      body: JSON.stringify({ charge_point_id: chargerId, transaction_id: transactionId }) 
    }),
  
  resetCharger: (chargerId, resetType = 'Soft') => 
    request('/api/ocpp/reset', { 
      method: 'POST', 
      body: JSON.stringify({ charge_point_id: chargerId, reset_type: resetType }) 
    }),
  
  unlockConnector: (chargerId, connectorId = 1) => 
    request('/api/ocpp/unlock', { 
      method: 'POST', 
      body: JSON.stringify({ charge_point_id: chargerId, connector_id: connectorId }) 
    }),
};

// -----------------------
// LICENSE SERVICE (Optional)
// -----------------------
export const licenseService = {
  list: async () => {
    try {
      return await request('/api/licenses');
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return [];
      throw error;
    }
  },
  get: async (id) => {
    try {
      return await request(`/api/licenses/${id}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  create: (data) => request('/api/licenses', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/licenses/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  getDistribution: async (siteId, month, year) => {
    return await request(`/api/licenses/distribution/${siteId}?month=${month}&year=${year}`);
  },
};

// -----------------------
// ENERGY SERVICE (Optional)
// -----------------------
export const energyService = {
  data: async () => {
    try {
      return await request('/api/energy/data');
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return [];
      throw error;
    }
  },
};

// -----------------------
// AUDIT SERVICE (Optional)
// -----------------------
export const auditService = {
  list: async (params = {}) => {
    try {
      const query = new URLSearchParams();
      if (params.action) query.append('action', params.action);
      if (params.user_id) query.append('user_id', params.user_id);
      if (params.limit) query.append('limit', params.limit.toString());
      const queryString = query.toString();
      const url = queryString ? `/api/audit-logs?${queryString}` : '/api/audit-logs';
      return await request(url);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return [];
      throw error;
    }
  },
  failedLogins: async (limit = 100) => {
    try {
      return await request(`/api/audit-logs/failed-logins?limit=${limit}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return [];
      throw error;
    }
  },
  userActivity: async (userId, limit = 50) => {
    try {
      return await request(`/api/audit-logs/user/${userId}?limit=${limit}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return [];
      throw error;
    }
  },
};

// -----------------------
// BACKUP SERVICE (Optional)
// -----------------------
export const backupService = {
  create: async (description = 'Manual backup') => {
    try {
      return await request(`/api/backup?description=${encodeURIComponent(description)}`, { method: 'POST' });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  list: async (limit = 20) => {
    try {
      return await request(`/api/backups?limit=${limit}`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return [];
      throw error;
    }
  },
  verify: async (backupId) => {
    try {
      return await request(`/api/backup/${backupId}/verify`);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  restore: async (backupId) => {
    try {
      return await request(`/api/backup/${backupId}/restore`, { method: 'POST' });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  delete: async (backupId) => {
    try {
      return await request(`/api/backup/${backupId}`, { method: 'DELETE' });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
};

// -----------------------
// SCHEDULER SERVICE (Optional)
// -----------------------
export const schedulerService = {
  jobs: async () => {
    try {
      return await request('/api/scheduler/jobs');
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return [];
      throw error;
    }
  },
  runJob: async (jobId) => {
    try {
      return await request(`/api/scheduler/jobs/${jobId}/run`, { method: 'POST' });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
  updateJob: async (jobId, data) => {
    try {
      const query = new URLSearchParams();
      if (data.enabled !== undefined) query.append('enabled', data.enabled.toString());
      if (data.interval_seconds !== undefined) query.append('interval_seconds', data.interval_seconds.toString());
      return await request(`/api/scheduler/jobs/${jobId}?${query.toString()}`, { method: 'PATCH' });
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null;
      throw error;
    }
  },
};

// -----------------------
// NOTIFICATION SERVICE
// -----------------------
export const notificationService = {
  list: async (unreadOnly = false, limit = 50) => {
    const params = new URLSearchParams();
    if (unreadOnly) params.append('unread_only', 'true');
    if (limit) params.append('limit', limit.toString());
    const queryString = params.toString();
    const url = queryString ? `/api/notifications?${queryString}` : '/api/notifications';
    return request(url);
  },

  getUnreadCount: () => request('/api/notifications/unread-count'),

  markAsRead: (notificationId) => 
    request(`/api/notifications/${notificationId}/read`, { method: 'POST' }),

  markAllAsRead: () => 
    request('/api/notifications/read-all', { method: 'POST' }),

  delete: (notificationId) => 
    request(`/api/notifications/${notificationId}`, { method: 'DELETE' }),

  getSettings: () => request('/api/notifications/settings'),

  updateSettings: (settings) => 
    request('/api/notifications/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings)
    }),
};

// -----------------------
// QR SERVICE
// -----------------------
export const qrService = {
  generate: (stationId) => request(`/api/qr/generate?station_id=${stationId}`, { method: 'POST' }),
};

// -----------------------
// PAYMENTS SERVICE
// -----------------------
export const paymentsService = {
  list: () => request('/api/payments'),
  create: (data) => request('/api/payments', { method: 'POST', body: JSON.stringify(data) }),
};

// -----------------------
// FAULT MANAGEMENT SERVICE
// -----------------------
export const faultsService = {
  // Faults
  list: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.priority) queryParams.append('priority', params.priority);
    if (params.station_id) queryParams.append('station_id', params.station_id);
    if (params.technician_id) queryParams.append('technician_id', params.technician_id);
    if (params.site_id) queryParams.append('site_id', params.site_id);
    if (params.include_closed !== undefined) queryParams.append('include_closed', params.include_closed);
    if (params.page) queryParams.append('page', params.page);
    if (params.page_size) queryParams.append('page_size', params.page_size);
    return request(`/api/faults?${queryParams.toString()}`);
  },
  get: (id) => request(`/api/faults/${id}`),
  create: (data) => request('/api/faults', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/faults/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/faults/${id}`, { method: 'DELETE' }),
  getHistory: (id) => request(`/api/faults/${id}/history`),
  getStats: (periodDays = 30) => request(`/api/faults/stats?period_days=${periodDays}`),
  getCritical: (limit = 5) => request(`/api/faults/critical?limit=${limit}`),
  seed: () => request('/api/faults/seed', { method: 'POST' }),
  
  // Photo management
  uploadPhoto: (faultId, photoBase64, description = '') => 
    request(`/api/faults/${faultId}/photos`, { 
      method: 'POST', 
      body: JSON.stringify({ photo_base64: photoBase64, description }) 
    }),
  getPhotoUrl: (faultId, photoId) => `${API_URL}/api/faults/${faultId}/photos/${photoId}`,
  deletePhoto: (faultId, photoId) => 
    request(`/api/faults/${faultId}/photos/${photoId}`, { method: 'DELETE' }),
};

// -----------------------
// TECHNICIANS SERVICE
// -----------------------
export const techniciansService = {
  list: (activeOnly = true) => request(`/api/technicians?active_only=${activeOnly}`),
  get: (id) => request(`/api/technicians/${id}`),
  create: (data) => request('/api/technicians', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => request(`/api/technicians/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => request(`/api/technicians/${id}`, { method: 'DELETE' }),
};

// -----------------------
// MODULE MANAGEMENT SERVICE
// -----------------------
export const moduleService = {
  list: () => request('/api/modules'),
  get: (siteId) => request(`/api/modules/${siteId}`),
  update: (siteId, data) => request(`/api/modules/${siteId}`, { method: 'PUT', body: JSON.stringify(data) }),
  getPermissions: () => request('/api/permissions'),
  // Kullanıcı bazlı izinler
  getUserPermissions: (userId) => request(`/api/modules/user/${userId}/permissions`),
  updateUserPermission: (userId, data) => request(`/api/modules/user/${userId}/permissions`, { method: 'PUT', body: JSON.stringify(data) }),
};

// -----------------------
// CONTENT SERVICE (Haber Merkezi)
// -----------------------
export const contentService = {
  // İçerik listesi
  list: (params = {}) => {
    const query = new URLSearchParams();
    if (params.category) query.append('category', params.category);
    if (params.published_only !== undefined) query.append('published_only', params.published_only);
    if (params.skip) query.append('skip', params.skip);
    if (params.limit) query.append('limit', params.limit);
    
    return request(`/api/content?${query.toString()}`);
  },

  // İçerik detayı
  get: (id) => request(`/api/content/${id}`),

  // İçerik oluştur
  create: (data) => request('/api/content', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // İçerik güncelle
  update: (id, data) => request(`/api/content/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // İçerik sil
  delete: (id) => request(`/api/content/${id}`, {
    method: 'DELETE',
  }),

  // Beğeni ekle/kaldır
  toggleLike: (id) => request(`/api/content/${id}/like`, {
    method: 'POST',
  }),

  // Kategorileri getir
  getCategories: () => request('/api/content-categories'),

  // AI: Makale taslağı oluştur
  generateDraft: (topic) => request('/api/ai/generate-draft', {
    method: 'POST',
    body: JSON.stringify({ topic }),
  }),

  // AI: Özet oluştur
  generateSummary: (body) => request('/api/ai/generate-summary', {
    method: 'POST',
    body: JSON.stringify({ body }),
  }),

  // AI: Sohbet
  chat: (message, articleTitle, articleSummary, chatHistory = []) => request('/api/ai/chat', {
    method: 'POST',
    body: JSON.stringify({ 
      message, 
      article_title: articleTitle,
      article_summary: articleSummary,
      chat_history: chatHistory
    }),
  }),
};

// -----------------------
// COMBINED API EXPORT
// -----------------------
const himsApi = {
  auth: authService,
  users: usersService,
  sites: sitesService,
  stations: stationsService,
  sessions: sessionsService,
  rfid: rfidService,
  pricing: pricingService,
  dashboard: dashboardService,
  reports: reportsService,
  ocpp: ocppService,
  ocppLogs: ocppLogsService,
  licenses: licenseService,
  energy: energyService,
  audit: auditService,
  backup: backupService,
  scheduler: schedulerService,
  qr: qrService,
  payments: paymentsService,
  notifications: notificationService,
  emailSettings: emailSettingsService,
  faults: faultsService,
  technicians: techniciansService,
  modules: moduleService,
  content: contentService,
};

export default himsApi;
